import type {
  EmbedGithubBlockUrlData,
  EmbedGithubModel,
} from '@blocksuite/affine-model';
import type { LinkPreviewProvider } from '@blocksuite/affine-shared/services';
import { isAbortError } from '@blocksuite/affine-shared/utils';
import { nothing } from 'lit';

import type { EmbedGithubBlockComponent } from './embed-github-block.js';
import {
  GithubIssueClosedFailureIcon,
  GithubIssueClosedSuccessIcon,
  GithubIssueOpenIcon,
  GithubPRClosedIcon,
  GithubPRDraftIcon,
  GithubPRMergedIcon,
  GithubPROpenIcon,
} from './styles.js';

/** The fields of a GitHub issue or pull request the card shows. */
interface GithubIssueApiData {
  state: string;
  state_reason: string | null;
  draft?: boolean;
  merged?: boolean;
  created_at: string;
  assignees: { login: string }[];
}

export async function queryEmbedGithubData(
  embedGithubModel: EmbedGithubModel,
  linkPreviewer: LinkPreviewProvider,
  signal?: AbortSignal
): Promise<Partial<EmbedGithubBlockUrlData>> {
  const [githubApiData, openGraphData] = await Promise.all([
    queryEmbedGithubApiData(embedGithubModel, linkPreviewer, signal),
    linkPreviewer.query(embedGithubModel.props.url, signal),
  ]);
  return { ...githubApiData, ...openGraphData };
}

export async function queryEmbedGithubApiData(
  embedGithubModel: EmbedGithubModel,
  linkPreviewer: LinkPreviewProvider,
  signal?: AbortSignal
): Promise<Partial<EmbedGithubBlockUrlData>> {
  const { owner, repo, githubType, githubId } = embedGithubModel.props;

  // through the StreamLain server: the reader's browser never contacts GitHub
  const apiUrl = `https://api.github.com/repos/${owner}/${repo}/${
    githubType === 'issue' ? 'issues' : 'pulls'
  }/${githubId}`;
  const githubApiJson = (await linkPreviewer.queryOEmbed(
    apiUrl,
    signal
  )) as GithubIssueApiData | null;
  if (!githubApiJson) return {};

  const { state, state_reason, draft, merged, created_at, assignees } =
    githubApiJson;

  let status = state;
  if (merged) {
    status = 'merged';
  } else if (state === 'open' && draft) {
    status = 'draft';
  }

  return {
    status,
    statusReason: state_reason,
    createdAt: created_at,
    assignees: assignees.map(assignee => assignee.login),
  };
}

export async function refreshEmbedGithubUrlData(
  embedGithubElement: EmbedGithubBlockComponent,
  signal?: AbortSignal
): Promise<void> {
  let image = null,
    status = null,
    statusReason = null,
    title = null,
    description = null,
    createdAt = null,
    assignees = null;

  try {
    embedGithubElement.loading = true;

    // TODO(@mirone): remove service
    const queryUrlData = embedGithubElement.service?.queryUrlData;
    if (!queryUrlData) {
      console.error(
        `Trying to refresh github url data, but the queryUrlData is not found.`
      );
      return;
    }

    const githubUrlData = await queryUrlData(embedGithubElement.model);
    ({
      image = null,
      status = null,
      statusReason = null,
      title = null,
      description = null,
      createdAt = null,
      assignees = null,
    } = githubUrlData);

    if (signal?.aborted) return;

    embedGithubElement.store.updateBlock(embedGithubElement.model, {
      image,
      status,
      statusReason,
      title,
      description,
      createdAt,
      assignees,
    });
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) return;
    throw Error;
  } finally {
    embedGithubElement.loading = false;
  }
}

export async function refreshEmbedGithubStatus(
  embedGithubElement: EmbedGithubBlockComponent,
  signal?: AbortSignal
) {
  // TODO(@mirone): remove service
  const queryApiData = embedGithubElement.service?.queryApiData;
  if (!queryApiData) {
    console.error(
      `Trying to refresh github status, but the queryApiData is not found.`
    );
    return;
  }

  const githubApiData = await queryApiData(embedGithubElement.model, signal);

  if (!githubApiData.status || signal?.aborted) return;

  embedGithubElement.store.updateBlock(embedGithubElement.model, {
    status: githubApiData.status,
    statusReason: githubApiData.statusReason,
    createdAt: githubApiData.createdAt,
    assignees: githubApiData.assignees,
  });
}

export function getGithubStatusIcon(
  type: 'issue' | 'pr',
  status: string,
  statusReason: string | null
) {
  if (type === 'issue') {
    if (status === 'open') {
      return GithubIssueOpenIcon;
    } else if (status === 'closed' && statusReason === 'completed') {
      return GithubIssueClosedSuccessIcon;
    } else if (status === 'closed' && statusReason === 'not_planned') {
      return GithubIssueClosedFailureIcon;
    } else {
      return nothing;
    }
  } else if (type === 'pr') {
    if (status === 'open') {
      return GithubPROpenIcon;
    } else if (status === 'draft') {
      return GithubPRDraftIcon;
    } else if (status === 'merged') {
      return GithubPRMergedIcon;
    } else if (status === 'closed') {
      return GithubPRClosedIcon;
    }
  }
  return nothing;
}
